import { Link, useParams } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { articles } from "@/mocks/articles";
import Footer from "@/pages/home/components/Footer";

export default function DynamicArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const articleSchema = useMemo(() => {
    if (!article) return null;
    const siteUrl = typeof window !== "undefined" ? window.location.origin : "https://kakobuyspreadsheet.gay";
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.excerpt,
      "author": {
        "@type": "Organization",
        "name": "Spreadsheet Experts Team"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Kakobuy Spreadsheet",
        "logo": {
          "@type": "ImageObject",
          "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png"
        }
      },
      "datePublished": "2026-06-25",
      "dateModified": "2026-06-25",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${siteUrl}/article/${article.slug}`
      },
      "image": article.image
    };
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="font-heading font-bold text-2xl text-foreground-950 mb-4">Article Not Found</h1>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-label font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {articleSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
        />
      )}
      <nav className="sticky top-0 left-0 w-full z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
              <span className="text-lg md:text-xl font-heading font-extrabold text-foreground-950 tracking-tight whitespace-nowrap">
                Kakobuy <span className="text-primary-500">Spreadsheet</span>
              </span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/" className="px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-foreground-800 text-sm font-label transition-colors cursor-pointer whitespace-nowrap">
                Home
              </Link>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-label font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      </nav>

      <main className="px-4 md:px-6 lg:px-8 py-8 md:py-12">
        <article className="max-w-3xl mx-auto">
          <nav className="mb-6" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">Home</Link>
              </li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">Kakobuy Spreadsheet</Link>
              </li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-800 font-medium">{article.title}</li>
            </ol>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-label font-semibold">
                {article.category}
              </span>
              <span className="text-xs text-foreground-500">Updated {article.date}</span>
            </div>

            <h1 className="font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-tight mb-6">
              {article.title}
            </h1>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-user-line text-primary-600 text-sm"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-900">Spreadsheet Experts Team</p>
                <p className="text-xs text-foreground-500">Reviewed by Buying Experts · Independent Resource</p>
              </div>
            </div>

            <div className="w-full h-48 md:h-64 rounded-xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </header>

          <div className="space-y-6 text-sm md:text-base text-foreground-700 leading-relaxed">
            {article.content.map((block, idx) => {
              if (block.type === "h2") {
                return (
                  <h2 key={idx} className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mt-10 mb-4">
                    {block.text}
                  </h2>
                );
              }
              if (block.type === "h3") {
                return (
                  <h3 key={idx} className="font-heading font-semibold text-lg text-foreground-900 mt-6 mb-2">
                    {block.text}
                  </h3>
                );
              }
              return (
                <p key={idx} dangerouslySetInnerHTML={{ __html: block.text }} />
              );
            })}
          </div>

          <section className="mt-12 p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70">
            <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-950 mb-3">
              Explore More Kakobuy Spreadsheet Resources
            </h2>
            <p className="text-sm text-foreground-700 mb-5">
              Browse our complete directory of 20,000+ verified products or read more guides to become a smarter shopper.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-label font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Back to Kakobuy Spreadsheet Home
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/what-is-kakobuy-spreadsheet"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-primary-200 text-primary-600 text-sm font-label font-semibold hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Read Shopping Guide
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}